import {ContentTransferEncodingType, uint8ArrayToBase64} from "../util/MimeUtil";
import utf8 from "utf8";
import quotedPrintable from "quoted-printable";
import {MimeHeaderType} from "./Mime";
import {BlobBuilder} from "./BlobBuilder";

export interface MimeHeader {
    type: MimeHeaderType;
    value: string;
}

export class MimeWriterPart {
    public contentType: string;
    public boundary?: string;
    public protocol?: string;
    public headers: MimeHeader[];

    private readonly subParts: MimeWriterPart[] = [];

    public content?: string;

    private partBuilder?: BlobBuilder;

    public constructor(contentType: string, headers?: MimeHeader[], content?: string | ArrayBuffer | Uint8Array, boundary?: string) {
        this.boundary = boundary;
        this.headers = headers ?? [];
        this.contentType = contentType;
        // TODO: convert the attachment data to base64 more efficiently!
        if (content instanceof ArrayBuffer) {
            this.content = uint8ArrayToBase64(new Uint8Array(content));
        } else {
            // noinspection SuspiciousTypeOfGuard
            if (content instanceof Uint8Array) {
                this.content = uint8ArrayToBase64(content);
            } else {
                this.content = content;
            }
        }
    }

    public static createInlinePart(contentType: string, encoding: ContentTransferEncodingType, content: string) {
        return new MimeWriterPart(contentType, [
            {type: "Content-Transfer-Encoding", value: encoding},
        ], content);
    }

    public static encodeIfRequired(text: string): string {
        let encodingRequired = false;
        for (let i = 0; i < text.length; i++) {
            const char = text.charCodeAt(i);
            if (char < 32 || char > 126 || char === 34) {    // 34 is "
                encodingRequired = true;
                break;
            }
        }
        if (encodingRequired) {
            return "=?UTF-8?Q?" + quotedPrintable.encode(utf8.encode(text.replace(" ", "_"))) + "?=";
        }
        return text;
    }

    public static createAttachmentPart(contentType: string, encoding: ContentTransferEncodingType, fileName: string, data: ArrayBuffer | string, contentId: string | null) {
        const encodedFileName = MimeWriterPart.encodeIfRequired(fileName);
        const fullContentType = `${contentType};\r\n\tname="${encodedFileName}"`;

        const headers: MimeHeader[] = [
            {type: "Content-Transfer-Encoding", value: encoding},
            {type: "Content-Description", value: encodedFileName},
            {type: "Content-Disposition", value: `attachment;\r\n\tfilename="${encodedFileName}"`},
        ];
        if (contentId) {
            headers.push({type: "Content-ID", value: `<${contentId}>`});
        }
        return new MimeWriterPart(fullContentType, headers, data);
    }

    public getContentType(): string {
        if (this.subParts.length > 0) {
            const protocolString = this.protocol ? `\r\n\tprotocol="${this.protocol}";` : "";
            return `${this.contentType};${protocolString}\r\n\tboundary="${this.boundary}"`;
        }
        return this.contentType;
    }

    public addSubPart(part: MimeWriterPart) {
        this.subParts.push(part);
    }

    public writeTo(builder: BlobBuilder): void {
        builder.append(this.content);

        if (this.content) {
            builder.append("\r\n\r\n");
        }

        for (const subPart of this.subParts) {
            builder.append(`--${this.boundary}\r\n`);
            subPart.writePartTo(builder);
            builder.append("\r\n");
        }

        builder.append(`--${this.boundary}--\r\n`);
    }

    public writePartTo(builder: BlobBuilder): void {
        if (!this.partBuilder) {
            this.partBuilder = new BlobBuilder();

            this.partBuilder.append(`Content-Type: ${this.getContentType()}\r\n`);

            for (const header of this.headers) {
                this.partBuilder.append(`${header.type}: ${header.value}\r\n`);
            }
            this.partBuilder.append("\r\n");

            if (this.subParts.length > 0) {
                this.writeTo(this.partBuilder);
            } else {
                this.partBuilder.append(this.content);
                // TODO: do we always need this?
                this.partBuilder.append("\r\n");
            }
        }
        this.partBuilder.copyTo(builder);
    }

    public asString(): Promise<string> {
        const builder = new BlobBuilder();
        this.writeTo(builder);
        return builder.convertToString();
    }

    public partAsString(): Promise<string> {
        const builder = new BlobBuilder();
        this.writePartTo(builder);
        return builder.convertToString();
    }
}
